package gr.tech.site.pages.article

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initCashbackforgrowersPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("article/cashbackforgrowers.md", FrontMatterElement.Builder { addScalar("title", "Cash Back for Growers: A Step-by-Step Roadmap to Utility Incentives"); addScalar("description", "In the highly competitive landscape of 2026 commercial cultivation, managing operational costs is the key to maintaining healthy margins. One of the most effective strategies to inject capital back into your business is through utility-backed incentives."); addScalar("author", "Dan Lineman"); addScalar("date", "2026-03-24"); addScalar("updated", "2026-03-24"); }))
}

@Page("/article/cash-back-for-growers-a-step-by-step-roadmap-to-utility-incentives")
@Layout(".components.layouts.ArticleLayout")
@Composable
fun CashbackforgrowersPage() {
    org.jetbrains.compose.web.dom.H1(attrs = { id("cash-back-for-growers-a-step-by-step-roadmap-to-utility-incentives") }) {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Cash Back for Growers: A Step-by-Step Roadmap to Utility Incentives")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("In the highly competitive landscape of 2026 commercial cultivation, managing operational costs is the key to maintaining healthy margins. One of the most effective strategies to inject capital back into your business is through ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("utility-backed incentives")
        }
        org.jetbrains.compose.web.dom.Text(". However, securing ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("cash back for growers")
        }
        org.jetbrains.compose.web.dom.Text(" is not as simple as submitting a receipt. It requires a technical, phased approach to meet the rigorous energy-saving mandates of North American utility providers.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("This roadmap outlines the exact engineering and administrative steps required to successfully navigate the incentive process and maximize your facility's ROI.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("phase-1-the-apply-before-you-buy-mandate") }) {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Phase 1: The \"Apply Before You Buy\" Mandate")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("If you want to rank for \"How to apply for rebates,\" the most critical lesson is timing. A common pitfall that costs cultivators hundreds of thousands of dollars is purchasing equipment before obtaining utility authorization. Most ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("utility-backed incentives")
        }
        org.jetbrains.compose.web.dom.Text(" are classified as \"discretionary,\" meaning the utility must prove that their money ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("caused")
        }
        org.jetbrains.compose.web.dom.Text(" you to choose the more efficient option.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The Pre-Approval Rule:")
        }
        org.jetbrains.compose.web.dom.Text(" You must submit your application, including your energy model and lighting layout, ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("before")
        }
        org.jetbrains.compose.web.dom.Text(" issuing a Purchase Order (P.O.) or making a down payment on fixtures. If the equipment is already on-site or paid for, the utility assumes the project would have happened without their help, and they will likely deny your claim for ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("cash back for growers")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("phase-2-technical-compliance-and-dlc-standards") }) {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Phase 2: Technical Compliance and DLC Standards")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("To qualify for top-tier ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("utility-backed incentives")
        }
        org.jetbrains.compose.web.dom.Text(", your equipment must meet specific performance benchmarks. In 2026, the primary authority for these benchmarks is the ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("DesignLights Consortium (DLC)")
        }
        org.jetbrains.compose.web.dom.Text(". Most utility programs require that horticultural lighting be listed on the ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("DLC Qualified Products List (QPL)")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Facility engineers must verify the following technical specifications to ensure eligibility:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Photosynthetic Photon Efficacy (PPE):")
            }
            org.jetbrains.compose.web.dom.Text(" Many 2026 programs now require a minimum PPE of ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("2.3 µmol/J")
            }
            org.jetbrains.compose.web.dom.Text(" or higher for prescriptive rebates, with \"Custom\" programs often incentivizing fixtures that exceed ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("2.8 µmol/J")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Spectral Quality:")
            }
            org.jetbrains.compose.web.dom.Text(" The DLC QPL ensures that the light spectrum is optimized for plant growth, preventing utilities from subsidizing low-quality industrial lighting that doesn't meet cultivation needs.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Warranty Requirements:")
            }
            org.jetbrains.compose.web.dom.Text(" Most ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("utility-backed incentives")
            }
            org.jetbrains.compose.web.dom.Text(" require a minimum 5-year manufacturer warranty to ensure the energy savings persist long-term.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("phase-3-comparing-utility-payout-models") }) {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Phase 3: Comparing Utility Payout Models")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("To maximize ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("cash back for growers")
        }
        org.jetbrains.compose.web.dom.Text(", you must understand the difference between ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("Prescriptive")
        }
        org.jetbrains.compose.web.dom.Text(" (flat rate per fixture) and ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("Custom")
        }
        org.jetbrains.compose.web.dom.Text(" (pay-for-performance) models. Custom models typically yield higher returns for high-intensity indoor grows with long photoperiods.")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Utility Provider / Region")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Payout Basis")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Est. Incentive Rate")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Primary Requirement")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("Eversource (Connecticut)")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Annual kWh Saved")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("${'$'}0.40 / kWh")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("DLC Horticultural QPL")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("National Grid (Massachusetts)")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Custom Energy Savings")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("${'$'}0.30 / kWh")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Pre-Installation Audit")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("Energy Trust of Oregon")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Per Fixture (LED)")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("${'$'}150 - ${'$'}300 / unit")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("HPS to LED Retrofit")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("ComEd (Illinois)")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Total Project Cost %")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("Up to 50% Coverage")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("New Build Energy Modeling")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("phase-4-final-documentation-and-measurement-verification-m-v") }) {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Phase 4: Final Documentation and Measurement & Verification (M&V)")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The roadmap to ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("cash back for growers")
        }
        org.jetbrains.compose.web.dom.Text(" concludes with the post-installation phase. Once your ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("utility-backed incentives")
        }
        org.jetbrains.compose.web.dom.Text(" are pre-approved and the equipment is installed, the utility may require a ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("Measurement and Verification (M&V)")
        }
        org.jetbrains.compose.web.dom.Text(" period. This involves:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Providing proof of purchase (invoices and cancelled checks).")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Submitting \"As-Built\" lighting and HVAC layouts.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("A final on-site inspection by a utility engineer to verify the DLC-listed model numbers match the application.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Once the inspection is cleared, the incentive check is typically issued within 60 to 90 days, providing a massive infusion of liquidity back into your operations.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("conclusion-start-your-roadmap-today") }) {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Conclusion: Start Your Roadmap Today")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The difference between a standard facility and a high-efficiency powerhouse is the strategy behind its funding. By prioritizing ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("DLC standards")
        }
        org.jetbrains.compose.web.dom.Text(" and strictly adhering to the ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Pre-Approval")
        }
        org.jetbrains.compose.web.dom.Text(" process, you ensure that you capture every available dollar of ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("cash back for growers")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Ready to find your rebate?")
        }
        org.jetbrains.compose.web.dom.Text(" Don't buy your next fixture without a plan. Contact ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://greenrebates.com/contact-us") {
            org.jetbrains.compose.web.dom.Text("Green Rebates")
        }
        org.jetbrains.compose.web.dom.Text(" for a professional assessment and let us handle the technical filings to secure your ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("utility-backed incentives")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Related:")
        }
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            com.varabyte.kobweb.silk.components.navigation.Link("/article/rebates-incentives-for-indoor-growers-what-you-need-to-know") {
                org.jetbrains.compose.web.dom.Text("Rebates vs. Incentives for Indoor Growers: What You Need to Know")
            }
        }
        org.jetbrains.compose.web.dom.Li {
            com.varabyte.kobweb.silk.components.navigation.Link("/article/why-you-should-use-a-rebate-company-for-your-horticulture-energy-rebates-and-incentives") {
                org.jetbrains.compose.web.dom.Text("Why You Should Use A Rebate Company for Your Horticulture Energy Rebates and Incentives")
            }
        }
        org.jetbrains.compose.web.dom.Li {
            com.varabyte.kobweb.silk.components.navigation.Link("/article/the-2026-guide-to-cannabis-rebates-how-to-get-paid-for-your-grow-upgrades") {
                org.jetbrains.compose.web.dom.Text("The 2026 Guide to Cannabis Rebates: How to Get Paid for Your Grow Upgrades")
            }
        }
    }
}
